import type { DbHealthReport } from '#shared/types';
import { formatCount, formatDateTime, formatNumber } from '#client/lib/format';

const BYTE_UNITS = ['B', 'kB', 'MB', 'GB', 'TB'];

/** 1024-based, one decimal above bytes: `1.5 MB`. */
export function formatBytes(value: number): string {
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < BYTE_UNITS.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(unit === 0 ? 0 : 1)} ${BYTE_UNITS[unit]}`;
}

export function formatMs(value: number): string {
  return `${formatNumber(value, { maxFractionDigits: 3 })} ms`;
}

/** A maintenance timestamp, or "Never" when Postgres has no record of one. */
export function formatWhen(value: string | null): string {
  return value ? formatDateTime(value) : 'Never';
}

/** The "Database" card's rows. */
export function databaseDetails(database: DbHealthReport['database']) {
  return [
    { label: 'Name', value: database.name },
    { label: 'Size', value: formatBytes(database.size_bytes) },
    {
      label: 'Cache hit ratio',
      value: database.cache_hit_pct === null ? '—' : `${database.cache_hit_pct}%`,
    },
    { label: 'Connections', value: `${database.connections} of ${database.max_connections}` },
    {
      label: 'Commits / rollbacks',
      value: `${formatCount(database.commits)} / ${formatCount(database.rollbacks)}`,
    },
    { label: 'Deadlocks', value: formatCount(database.deadlocks) },
    {
      label: 'Temporary files',
      value: `${formatCount(database.temp_files)} (${formatBytes(database.temp_bytes)})`,
    },
    { label: 'Statistics since', value: formatWhen(database.stats_reset) },
  ];
}

/** One sentence naming every query stuck behind a lock, and what holds it. */
export function blockedSummary(blocked: DbHealthReport['blocked']): string {
  const subject = blocked.length === 1 ? 'query is' : 'queries are';
  const queries = blocked
    .map(
      (query) =>
        `pid ${query.pid} for ${query.waiting_seconds} s, blocked by ${query.blocked_by.join(', ')}`,
    )
    .join('; ');
  return `${blocked.length} ${subject} waiting on a lock: ${queries}.`;
}
